import { IAccessRequest } from 'interfaces/AccessRequest/IAccessRequest';
import { IAccessRequestUpdate } from 'interfaces/AccessRequest/IAccessRequestUpdate';
import { useCallback } from 'react';
import { pricelistRoute } from '../../helpers/apiRoutes';
import { useApiCalls } from '../UseApiCalls';

export const PricelistApis = () => {
  const { getQuery, postQuery, getQueryFileDownload } = useApiCalls();

  const getMasterPricelist = useCallback(() => {
    return getQuery(`${pricelistRoute}/get-master-pricelist`);
  }, [getQuery]);

  const getPricelistFilters = useCallback(() => {
    return getQuery(`${pricelistRoute}/get-pricelist-filters`);
  }, [getQuery]);

  const getAvailablePricelists = useCallback(() => {
    return getQuery(`${pricelistRoute}/get-available-pricelists`);
  }, [getQuery]);

  const getAllPricelists = useCallback(() => {
    return getQuery(`${pricelistRoute}/get-all-pricelists`);
  }, [getQuery]);

  const getClientPricelist = useCallback(
    (client) => {
      return getQuery(`${pricelistRoute}/get-client-pricelist/${encodeURIComponent(client)}`);
    },
    [getQuery]
  );

  const getClientPricelistDownload = useCallback(
    (client) => {
      return getQueryFileDownload(`${pricelistRoute}/download-client-pricelist/${encodeURIComponent(client)}`);
    },
    [getQueryFileDownload]
  );

  const getAnalysisExcelDownload = useCallback((analysis, matrix) => {
    return getQueryFileDownload(`${pricelistRoute}/download-analysis-details/${encodeURIComponent(analysis)}/${encodeURIComponent(matrix)}`);
  }, [getQueryFileDownload]);

  const getAnalysisInfo = useCallback(
    (analysis, matrix) => {
      return getQuery(`${pricelistRoute}/get-analysis-info/${encodeURIComponent(analysis)}/${encodeURIComponent(matrix)}`);
    },
    [getQuery]
  );

  const getAnalyteInfo = useCallback(
    (analysis, matrix) => {
      return getQuery(`${pricelistRoute}/get-analyte-info/${encodeURIComponent(analysis)}/${encodeURIComponent(matrix)}`);
    },
    [getQuery]
  );

  const getGroupAnalysisInfo = useCallback(
    (analysis, matrix) => {
      return getQuery(`${pricelistRoute}/get-group-analysis-info/${encodeURIComponent(analysis)}/${encodeURIComponent(matrix)}`);
    },
    [getQuery]
  );

  const getPrepAnalysisInfo = useCallback(
    (analysis, matrix) => {
      return getQuery(`${pricelistRoute}/get-prep-analysis-info/${encodeURIComponent(analysis)}/${encodeURIComponent(matrix)}`);
    },
    [getQuery]
  );

  const getSubAnalysisInfo = useCallback(
    (analysis, matrix) => {
      return getQuery(`${pricelistRoute}/get-sub-analysis-info/${encodeURIComponent(analysis)}/${encodeURIComponent(matrix)}`);
    },
    [getQuery]
  );

  const getSubAnalysisAnalyteInfo = useCallback(
    (analysis, matrix, analyte, isGroupAnalysis) => {
      return getQuery(
        `${pricelistRoute}/get-sub-analysis-analyte-info/${encodeURIComponent(analysis)}/${encodeURIComponent(matrix)}/${encodeURIComponent(
          analyte
        )}/${isGroupAnalysis}`
      );
    },
    [getQuery]
  );

  const getMediaInfo = useCallback(
    (analysis, matrix) => {
      return getQuery(`${pricelistRoute}/get-media-info/${encodeURIComponent(analysis)}/${encodeURIComponent(matrix)}`);
    },
    [getQuery]
  );

  const getTrackingAnalysisDetails = useCallback(
    (trackingAnalysis) => {
      return getQuery(`${pricelistRoute}/get-tracking-analysis-details/${encodeURIComponent(trackingAnalysis)}`);
    },
    [getQuery]
  );

  const getOtherInfo = useCallback(
    (analysis, matrix) => {
      return getQuery(`${pricelistRoute}/get-other-info/${encodeURIComponent(analysis)}/${encodeURIComponent(matrix)}`);
    },
    [getQuery]
  );

  const getManagedClients = useCallback(() => {
    return getQuery(`${pricelistRoute}/get-managed-clients`);
  }, [getQuery]);

  const getAccess = useCallback(() => {
    return getQuery(`${pricelistRoute}/get-access`);
  }, [getQuery]);

  const getClientAccess = useCallback(
    (client) => {
      return getQuery(`${pricelistRoute}/get-client-access/${encodeURIComponent(client)}`);
    },
    [getQuery]
  );

  const postRequestAccess = useCallback(
    (accessId) => {
      const data = {
        Id: accessId,
      };
      return postQuery(`${pricelistRoute}/request-access`, data);
    },
    [postQuery]
  );

  const postSetPricelistAccess = useCallback((data: IAccessRequest) => {
    return fetch(`${pricelistRoute}/set-access`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }).then((data) => data.json());
  }, []);

  const getPricelistAccessDetails = useCallback((data: IAccessRequest) => {
    return fetch(`${pricelistRoute}/get-access-request-details/${data.Id}/${data.Token}/${data.AccessType}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((data) => data.json());
  }, []);

  const postUpdateAccess = useCallback(
    (data: IAccessRequestUpdate) => {
      return postQuery(`${pricelistRoute}/update-access`, data);
    },
    [postQuery]
  );

  return {
    getClientPricelistDownload,
    getMasterPricelist,
    getPricelistFilters,
    getAvailablePricelists,
    getClientPricelist,
    getAnalysisInfo,
    getAnalyteInfo,
    getGroupAnalysisInfo,
    getPrepAnalysisInfo,
    getSubAnalysisInfo,
    getSubAnalysisAnalyteInfo,
    getMediaInfo,
    getTrackingAnalysisDetails,
    getOtherInfo,
    getAllPricelists,
    getAccess,
    getManagedClients,
    getClientAccess,
    postRequestAccess,
    postSetPricelistAccess,
    postUpdateAccess,
    getPricelistAccessDetails,
    getAnalysisExcelDownload
  };
};
